import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  FirmInput,
  FirmType,
  FirmWithUsers,
  MarketplaceUserRole,
  ServiceType,
  createFirm,
  deactivateFirmUser,
  inviteFirmUser,
  listFirms,
  patchFirm,
} from '../../api/marketplace';
import {
  FIRM_TYPES,
  FIRM_TYPE_LABELS,
  SERVICE_LABELS,
  SERVICE_TYPES,
  parseCodes,
  serviceLabel,
  toDate,
} from './marketplaceFormat';
import './Marketplace.css';

interface FirmFormValues {
  name: string;
  type: FirmType;
  services: ServiceType[];
  languages: string;
  oaNumber: string;
  contactEmail: string;
  agreementSignedAt: string;
  dpaSignedAt: string;
}

const EMPTY_FIRM: FirmFormValues = {
  name: '',
  type: 'law_firm',
  services: [],
  languages: '',
  oaNumber: '',
  contactEmail: '',
  agreementSignedAt: '',
  dpaSignedAt: '',
};

const dateInput = (value: FirmWithUsers['agreementSignedAt']): string => {
  const date = toDate(value);
  return date ? date.toISOString().slice(0, 10) : '';
};

const firmToValues = (firm: FirmWithUsers): FirmFormValues => ({
  name: firm.name || '',
  type: firm.type || 'law_firm',
  services: firm.services || [],
  languages: (firm.languages || []).join(', '),
  oaNumber: firm.oaNumber || '',
  contactEmail: firm.contactEmail || '',
  agreementSignedAt: dateInput(firm.agreementSignedAt),
  dpaSignedAt: dateInput(firm.dpaSignedAt),
});

const valuesToInput = (values: FirmFormValues): FirmInput => {
  const input: FirmInput = {
    name: values.name.trim(),
    type: values.type,
    services: values.services,
    languages: parseCodes(values.languages, false),
    contactEmail: values.contactEmail.trim(),
  };
  if (values.oaNumber.trim()) input.oaNumber = values.oaNumber.trim();
  if (values.agreementSignedAt) input.agreementSignedAt = new Date(values.agreementSignedAt).toISOString();
  if (values.dpaSignedAt) input.dpaSignedAt = new Date(values.dpaSignedAt).toISOString();
  return input;
};

const FirmForm: React.FC<{
  initial: FirmFormValues;
  submitLabel: string;
  onSubmit: (input: FirmInput) => Promise<void>;
  onCancel: () => void;
}> = ({ initial, submitLabel, onSubmit, onCancel }) => {
  const [values, setValues] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = <K extends keyof FirmFormValues>(key: K, value: FirmFormValues[K]) =>
    setValues(prev => ({ ...prev, [key]: value }));

  const toggleService = (service: ServiceType) =>
    set(
      'services',
      values.services.includes(service)
        ? values.services.filter(s => s !== service)
        : [...values.services, service]
    );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!values.name.trim() || !values.contactEmail.trim()) {
      setError('Name and contact email are required.');
      return;
    }
    if (values.services.length === 0) {
      setError('Pick at least one service.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await onSubmit(valuesToInput(values));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the firm.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="marketplace-form" onSubmit={handleSubmit} aria-label="Firm">
      {error && <div className="status-error-banner" role="alert">{error}</div>}
      <div className="marketplace-form-row">
        <label>
          Name
          <input type="text" value={values.name} onChange={e => set('name', e.target.value)} required />
        </label>
        <label>
          Type
          <select value={values.type} onChange={e => set('type', e.target.value as FirmType)}>
            {FIRM_TYPES.map(type => (
              <option key={type} value={type}>{FIRM_TYPE_LABELS[type]}</option>
            ))}
          </select>
        </label>
        <label>
          Contact email
          <input
            type="email"
            value={values.contactEmail}
            onChange={e => set('contactEmail', e.target.value)}
            required
          />
        </label>
      </div>
      <div className="marketplace-checkboxes" role="group" aria-label="Services">
        <span>Services</span>
        {SERVICE_TYPES.map(service => (
          <label key={service} className="marketplace-checkbox">
            <input
              type="checkbox"
              checked={values.services.includes(service)}
              onChange={() => toggleService(service)}
            />
            {SERVICE_LABELS[service]}
          </label>
        ))}
      </div>
      <div className="marketplace-form-row">
        <label>
          Languages (ISO codes)
          <input
            type="text"
            value={values.languages}
            onChange={e => set('languages', e.target.value)}
            placeholder="en, pt, de"
          />
        </label>
        <label>
          OA number
          <input type="text" value={values.oaNumber} onChange={e => set('oaNumber', e.target.value)} />
        </label>
        <label>
          Agreement signed
          <input
            type="date"
            value={values.agreementSignedAt}
            onChange={e => set('agreementSignedAt', e.target.value)}
          />
        </label>
        <label>
          DPA signed
          <input type="date" value={values.dpaSignedAt} onChange={e => set('dpaSignedAt', e.target.value)} />
        </label>
      </div>
      <div className="marketplace-actions">
        <button type="submit" className="download-button" disabled={busy}>{submitLabel}</button>
        <button type="button" className="logout-button" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
};

const InviteForm: React.FC<{ onInvite: (email: string, role: MarketplaceUserRole) => Promise<void> }> = ({
  onInvite,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MarketplaceUserRole>('member');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError('');
    try {
      await onInvite(email.trim().toLowerCase(), role);
      setEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite the user.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="marketplace-invite" onSubmit={handleSubmit} aria-label="Invite user">
      <input
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="name@firm.com"
        aria-label="Email"
        required
      />
      <select value={role} onChange={e => setRole(e.target.value as MarketplaceUserRole)} aria-label="Role">
        <option value="member">Member</option>
        <option value="owner">Owner</option>
      </select>
      <button type="submit" className="download-button" disabled={busy}>Invite</button>
      {error && <span className="marketplace-error-inline" role="alert">{error}</span>}
    </form>
  );
};

const AdminFirmsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [firms, setFirms] = useState<FirmWithUsers[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const token = useCallback(async () => {
    if (!currentUser) throw new Error('You are signed out.');
    return currentUser.getIdToken();
  }, [currentUser]);

  const load = useCallback(async () => {
    if (!currentUser) return;
    setError('');
    try {
      setFirms(await listFirms(await token()));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load firms.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, token]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (label: string, fn: (t: string) => Promise<unknown>) => {
    setError('');
    setNotice('');
    try {
      await fn(await token());
      setNotice(label);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  };

  return (
    <div className="marketplace-page">
      <Link to="/marketplace/admin/requests" className="service-link">Back to requests</Link>
      <div className="task-view-header">
        <h2>Marketplace firms</h2>
        {!creating && (
          <button type="button" className="download-button" onClick={() => setCreating(true)}>
            New firm
          </button>
        )}
      </div>
      {error && <div className="status-error-banner" role="alert">{error}</div>}
      {notice && <div className="marketplace-notice" role="status">{notice}</div>}
      {creating && (
        <section className="marketplace-panel">
          <h3>New firm</h3>
          <FirmForm
            initial={EMPTY_FIRM}
            submitLabel="Create firm"
            onSubmit={async input => {
              await createFirm(await token(), input);
              setCreating(false);
              setNotice('Firm created.');
              await load();
            }}
            onCancel={() => setCreating(false)}
          />
        </section>
      )}
      {loading ? (
        <p>Loading firms...</p>
      ) : firms.length === 0 ? (
        <p className="marketplace-empty">No firms yet.</p>
      ) : (
        firms.map(firm => (
          <section key={firm.id} className="marketplace-panel" aria-label={firm.name}>
            <div className="marketplace-card-head">
              <h3>
                {firm.name}{' '}
                <span className="marketplace-muted">{FIRM_TYPE_LABELS[firm.type] || firm.type}</span>
              </h3>
              <div className="marketplace-actions">
                <label className="marketplace-checkbox">
                  <input
                    type="checkbox"
                    checked={firm.active}
                    onChange={() =>
                      act(firm.active ? 'Firm deactivated.' : 'Firm activated.', t =>
                        patchFirm(t, firm.id, { active: !firm.active })
                      )
                    }
                  />
                  Active
                </label>
                {editingId !== firm.id && (
                  <button type="button" className="logout-button" onClick={() => setEditingId(firm.id)}>
                    Edit
                  </button>
                )}
              </div>
            </div>
            {editingId === firm.id ? (
              <FirmForm
                initial={firmToValues(firm)}
                submitLabel="Save firm"
                onSubmit={async input => {
                  await patchFirm(await token(), firm.id, input);
                  setEditingId(null);
                  setNotice('Firm saved.');
                  await load();
                }}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <dl className="marketplace-facts">
                <div><dt>Services</dt><dd>{(firm.services || []).map(serviceLabel).join(', ') || 'n/a'}</dd></div>
                <div><dt>Languages</dt><dd>{(firm.languages || []).join(', ').toUpperCase() || 'n/a'}</dd></div>
                <div><dt>Contact</dt><dd>{firm.contactEmail}</dd></div>
                <div><dt>OA number</dt><dd>{firm.oaNumber || 'n/a'}</dd></div>
                <div><dt>Agreement</dt><dd>{dateInput(firm.agreementSignedAt) || 'Not signed'}</dd></div>
                <div><dt>DPA</dt><dd>{dateInput(firm.dpaSignedAt) || 'Not signed'}</dd></div>
              </dl>
            )}
            <h4>Users</h4>
            {(firm.users || []).length === 0 ? (
              <p className="marketplace-hint">No users yet.</p>
            ) : (
              <table className="marketplace-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {firm.users.map(user => (
                    <tr key={user.id}>
                      <td>{user.email}</td>
                      <td>{user.role === 'owner' ? 'Owner' : 'Member'}</td>
                      <td>{!user.active ? 'Deactivated' : user.uid ? 'Active' : 'Invited'}</td>
                      <td>
                        {user.active && (
                          <button
                            type="button"
                            className="logout-button"
                            onClick={() => {
                              if (!window.confirm(`Deactivate ${user.email}?`)) return;
                              act('User deactivated.', t => deactivateFirmUser(t, firm.id, user.id));
                            }}
                          >
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <InviteForm
              onInvite={async (email, role) => {
                await inviteFirmUser(await token(), firm.id, email, role);
                setNotice(`Invite sent to ${email}.`);
                await load();
              }}
            />
          </section>
        ))
      )}
    </div>
  );
};

export default AdminFirmsPage;
